import React, { useState, useRef, useEffect } from 'react';
import { SketchPicker } from 'react-color'
import { makeStyles, withStyles } from '@material-ui/core/styles';

import Slider from '@material-ui/core/Slider';
import Grid from '@material-ui/core/Grid';
import Collapse from '@material-ui/core/Collapse';
import Skeleton from '@material-ui/lab/Skeleton';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import InputBase from '@material-ui/core/InputBase';
import Popover from '@material-ui/core/Popover';
import SvgIcon from '@material-ui/core/SvgIcon';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import TextField from '@material-ui/core/TextField';

import AlignLeft from '@material-ui/icons/FormatAlignLeft';
import AlignCenter from '@material-ui/icons/FormatAlignCenter';
import AlignRight from '@material-ui/icons/FormatAlignRight';

const RichImageSlider = withStyles(theme => ({
    root: {
        color: theme.palette.grey[600],
    },
    thumb: {
        backgroundColor: '#fff',
        border: '2px solid currentColor',
        '&:focus, &:hover, &$active': {
            boxShadow: 'inherit',
        },
    },
}))(Slider);

function ValueLabelComponent(props) {
    const { children, open, value } = props;

    return (
        <Tooltip open={open} enterTouchDelay={0} placement="bottom" title={value}>
            {children}
        </Tooltip>
    );
}

const useStyles = makeStyles(theme => ({
    container: {
        position: 'relative'
    },
    toolbar: {
        padding: theme.spacing(1),
        backgroundColor: '#fff',
        borderTop: `1px solid #ddd`,
        borderBottom: `1px solid #ddd`
    },
    toolbarItem: {
        lineHeight: 0
    },
    active: {
        backgroundColor: 'rgba(0, 0, 0, 0.10);',
        "&:hover" : {
            backgroundColor: 'background-color: rgba(0, 0, 0, 0.10);',
        }
    },
    buttonGroupBorder: {
        border: `1px solid rgba(0, 0, 0, 0.23)`,
        borderRadius: theme.shape.borderRadius,
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        paddingTop: 1,
        paddingBottom: 1
    },
    input: {
        minWidth: 'auto',

        '& input[type=number]': {
            textAlign: 'center',
            width: 30,
            '-webkit-appearance': 'none',
            margin: 0
        },
        '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
            '-webkit-appearance': 'none',
            margin: 0
        }
    },
    btn: {
        padding: '4px 9px'
    },
    root: {
        lineHeight: 0,
    },
}));

export default function RichImageEditor(props) {

    const { options, active, handleOptions, type } = props;

    const {
        background_color,
        image_align,
        image_width,
        image_width_type,
        padding_top,
        padding_left,
        padding_right,
        padding_bottom,
        link,
        link_url,
        target
    } = options;

    const pixels = (image_width_type === 'pixel') ? true : false;
    const maxWidth = (type === 'page') ? 1200 : 600;
    const step = (type === 'page') ? 40 : 20; 

    const file = options.content;

    const imageEl = useRef(null);

    const [ loading, setLoading ] = useState(false);
    const [ colorPickerAnchorEl, setColorPickerAnchorEl ] = useState(null);

    const classes = useStyles();

    useEffect(() => {
        if (file) {
      
            setLoading(true);

            const img = new Image();

            img.onload = () => setLoading(false);

            img.src = file.full_url;
        }
    }, []);

    const handlePadding = (e, name) => {
        handleOptions(name, parseInt(e.target.value) || 0);
    }

    const handleColor = (color) => {
        if (color && color.hex) {
            handleOptions('background_color', color.hex);
        }
    }

    const toggleImageWidth = (type) => {
        handleOptions('image_width_type', type);
        handleOptions('image_width', type === 'percent' ? 100 : 600)
    }

    // Color Picker Popover....................

    const openColorPicker = (event) => {
        event.preventDefault();
        setColorPickerAnchorEl(event.currentTarget);
    }

    const closeColorPicker= () => {
        setColorPickerAnchorEl(null);
    }

    const selectAll = (e) => e.target.select();

    return (
        <div className={classes.container}>
            <Collapse in={active}>
                <div className={classes.toolbar}>
                    <Grid container alignItems="center" spacing={1}>
                        <Grid item>
                            <ButtonGroup>
                                <Tooltip title='Align Image Left'>
                                    <Button
                                        size="small"
                                        className={image_align === 'left' ? classes.active : null}
                                        onClick={() => handleOptions('image_align', 'left')}
                                        >
                                        <AlignLeft />
                                    </Button>
                                </Tooltip>
                                <Tooltip title='Align Image Center'>
                                    <Button
                                        size="small"
                                        className={image_align === 'center' ? classes.active : null}
                                        onClick={() => handleOptions('image_align', 'center')}
                                        >
                                        <AlignCenter />
                                    </Button>
                                </Tooltip>
                                <Tooltip title='Align Image Right'>
                                    <Button
                                        size="small"
                                        className={image_align === 'right' ? classes.active : null}
                                        onClick={() => handleOptions('image_align', 'right')}
                                        >
                                        <AlignRight />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Grid>
                        <Grid item>
                            <ButtonGroup>
                                <Tooltip title='Image Width in Pixels'>
                                    <Button
                                        size="small"
                                        className={image_width_type === 'pixel' ? classes.active : null}
                                        onClick={() => toggleImageWidth('pixel')}
                                        >
                                        <strong>px</strong>
                                    </Button>
                                </Tooltip>
                                <Tooltip title='Image Width in Percentage'>
                                    <Button
                                        size="small"
                                        className={image_width_type === 'percent' ? classes.active : null}
                                        onClick={() => toggleImageWidth('percent')}
                                        >
                                        <strong>%</strong>
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Grid>
                        <Grid item xs={3} className={classes.toolbarItem}>
                            <div className={classes.buttonGroupBorder}>
                                <RichImageSlider
                                    valueLabelDisplay="auto"
                                    value={image_width}
                                    min={pixels ? step : 5}
                                    step={pixels ? step : 5}
                                    max={pixels ? maxWidth : 100}
                                    marks
                                    aria-label="Scale Image"
                                    onChange={(e, value) => handleOptions('image_width', value)}
                                    ValueLabelComponent={ValueLabelComponent}
                                />
                            </div>
                        </Grid>
                        <Grid item>
                            <div className={classes.buttonGroupBorder}>
                                <Tooltip title="Padding Top">
                                    <InputBase
                                        value={padding_top}
                                        onChange={(e) => handlePadding(e, 'padding_top')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                    />
                                </Tooltip>
                                <Tooltip title="Padding Bottom">
                                    <InputBase
                                        value={padding_bottom}
                                        onChange={(e) => handlePadding(e, 'padding_bottom')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                    />
                                </Tooltip>
                                <Tooltip title="Padding Left">
                                    <InputBase
                                        value={padding_left}
                                        onChange={(e) => handlePadding(e, 'padding_left')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                    />
                                </Tooltip>
                                <Tooltip title="Padding Right">
                                    <InputBase
                                        value={padding_right}
                                        onChange={(e) => handlePadding(e, 'padding_right')}
                                        className={classes.input}
                                        color="primary"
                                        type="number"
                                        onFocus={selectAll}
                                    />
                                </Tooltip>
                            </div>
                        </Grid>
                        <Grid item>
                            <ButtonGroup>
                                <Tooltip title="Background Color">
                                    <Button className={classes.btn} aria-label="Background Color" size="small" onClick={event => openColorPicker(event)}>
                                        <SvgIcon>
                                            <path d="M16.56 8.94L7.62 0 6.21 1.41l2.38 2.38-5.15 5.15c-.59.59-.59 1.54 0 2.12l5.5 5.5c.29.29.68.44 1.06.44s.77-.15 1.06-.44l5.5-5.5c.59-.58.59-1.53 0-2.12zM5.21 10L10 5.21 14.79 10H5.21zM19 11.5s-2 2.17-2 3.5c0 1.1.9 2 2 2s2-.9 2-2c0-1.33-2-3.5-2-3.5z"/>
                                            <path style={{ color: background_color }} d="M0 20h24v4H0z"/>
                                        </SvgIcon>
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                            <Popover
                                open={Boolean(colorPickerAnchorEl)}
                                anchorEl={colorPickerAnchorEl}
                                onClose={closeColorPicker}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'center',
                                }}
                                disableAutoFocus
                                disableEnforceFocus
                                disableRestoreFocus
                                >
                                <SketchPicker color={background_color} onChange={handleColor} disableAlpha />
                            </Popover>
                        </Grid>
                        <Grid item>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={link}
                                        onChange={(e) => handleOptions('link', !link)}
                                        name="link"
                                        color="primary"
                                    />
                                }
                                label="Add a Link?"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                value={link_url}
                                onChange={(e) => handleOptions('link_url', e.target.value)}
                                variant="outlined"
                                size="small"
                                placeholder="https://example.com"
                                label="Full URL"
                                fullWidth
                                disabled={!link}
                                >
                            </TextField>
                        </Grid>
                    </Grid>
                </div>
            </Collapse>
            {loading ? (
                <Skeleton animation="wave" variant="rect" width="100%" height={200} />
            ) : (
                <div className={classes.root} onClick={focus} style={{
                    backgroundColor: options.background_color,
                    paddingTop: options.padding_top,
                    paddingBottom: options.padding_bottom,
                    paddingLeft: options.padding_left,
                    paddingRight: options.padding_right,
                    textAlign: options.image_align
                    }}
                    >
                    <img
                        src={file.full_url}
                        style={{
                            maxWidth: '100%',
                            width: pixels ? image_width : image_width + '%',
                            height: 'auto'
                        }}
                        ref={imageEl}
                    />
                </div>
            )}
        </div>
    )
}
